'use client';

import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * JS scroll anchoring for a scroll container whose children churn (items added/removed above the
 * viewport). Keeps the topmost visible child pinned so the view doesn't jump — the same thing the
 * native CSS `overflow-anchor` does, but implemented by hand because Safari/iOS WebKit ships no
 * `overflow-anchor` support (and there's no polyfill). Coexists with native anchoring on
 * Chrome/Android: there the anchor is already held, so the correction below measures ~0 and no-ops.
 *
 * Each anchorable child must carry a `data-anchor-id` attribute (any stable value). The hook picks
 * the first child at/below the viewport top as the anchor, remembers its offset, and after every
 * list change (keyed by `signature`) shifts `scrollTop` by however far that same child moved.
 */
export function useScrollAnchor(
  scrollRef: RefObject<HTMLElement | null>,
  signature: string,
): void {
  // The pinned child + its top offset within the scroll viewport, as of the last measurement.
  const anchor = useRef<{ el: HTMLElement; top: number } | null>(null);

  const record = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const base = sc.getBoundingClientRect().top;
    for (const el of sc.querySelectorAll<HTMLElement>('[data-anchor-id]')) {
      const top = el.getBoundingClientRect().top - base;
      // first card whose top is at/just below the viewport top — a stable anchor
      if (top >= -1) {
        anchor.current = { el, top };
        return;
      }
    }
    anchor.current = null;
  }, [scrollRef]);

  // Before paint after a list change: if the anchored card survived, offset scrollTop by its drift.
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const prev = anchor.current;
    if (prev && prev.el.isConnected && sc.contains(prev.el)) {
      const now = prev.el.getBoundingClientRect().top - sc.getBoundingClientRect().top;
      const delta = now - prev.top;
      if (Math.abs(delta) > 0.5) sc.scrollTop += delta;
    }
    record();
  }, [signature, record, scrollRef]);

  // Keep the anchor fresh as the user scrolls, so a list change restores to where they actually are.
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    record();
    const onScroll = () => record();
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [record, scrollRef]);
}
